import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useNotifications } from '../contexts/NotificationsContext';
import { NotificationItem } from '../types';

function labelFor(item: NotificationItem): string {
  if (item.processing) {
    return item.note_type ? `${item.note_type} note — generating…` : 'Note generating…';
  }
  return item.note_type ? `${item.note_type} note ready` : 'Note ready';
}

function relativeTime(iso: string): string {
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const diffMin = Math.floor(diffMs / 60_000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
  } catch {
    return '';
  }
}

const HeaderNotificationsBell = () => {
  const navigate = useNavigate();
  const { items, unread, markPanelOpened, clearAll } = useNotifications();

  const [open, setOpen] = React.useState(false);
  const [shouldRender, setShouldRender] = React.useState(false);
  const [enter, setEnter] = React.useState(false);

  const hasClearable = items.some(p => !p.processing);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as Element)?.closest?.('[data-notif-dropdown-root]')) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  React.useEffect(() => {
    if (open) {
      setShouldRender(true);
      setEnter(false);
      const rAF = requestAnimationFrame(() => setEnter(true));
      return () => cancelAnimationFrame(rAF);
    } else if (shouldRender) {
      setEnter(false);
      const t = setTimeout(() => setShouldRender(false), 320);
      return () => clearTimeout(t);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) markPanelOpened();
  };

  const handleItemClick = (item: NotificationItem) => {
    if (item.processing) return;
    setOpen(false);
    if (item.href) navigate(item.href);
  };

  return (
    <div className='relative' data-notif-dropdown-root>
      <button
        type='button'
        aria-label={unread > 0 ? `${unread} unread notifications` : 'Notifications'}
        aria-haspopup='menu'
        aria-expanded={open}
        onClick={handleOpen}
        className='relative flex items-center justify-center h-8 w-8 rounded-full text-brand-navy/70 hover:text-brand-navy hover:bg-brand-navy/10 focus:outline-none focus:ring-2 focus:ring-brand-accent transition-colors'
      >
        <BellIcon className='h-5 w-5' />
        {unread > 0 && (
          <span
            aria-hidden='true'
            className='absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white leading-none'
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {shouldRender && (
        <div
          role='menu'
          aria-label='Notifications'
          aria-hidden={!open}
          className={`absolute right-0 mt-2 w-80 bg-white border border-brand-navy/15 ring-1 ring-brand-navy/15 rounded-xl shadow-lg z-50 origin-top-right transform transition-all duration-[260ms] ease-out ${
            enter ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
          }`}
        >
          <div className='px-3 py-2.5 border-b border-brand-navy/10 flex items-center justify-between'>
            <span className='text-xs font-semibold text-brand-navy uppercase tracking-wide'>Notifications</span>
            {hasClearable && (
              <button
                type='button'
                onClick={clearAll}
                className='text-[11px] text-brand-navy/50 hover:text-brand-navy focus:outline-none focus:underline'
              >
                Clear all
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className='py-10 flex flex-col items-center gap-2 text-gray-400'>
              <BellIcon className='h-8 w-8 text-gray-200' />
              <p className='text-xs'>No notifications</p>
            </div>
          ) : (
            <ul className='max-h-72 overflow-y-auto divide-y divide-brand-navy/5'>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    role='menuitem'
                    type='button'
                    onClick={() => handleItemClick(item)}
                    disabled={item.processing}
                    className={`w-full text-left px-3 py-3 focus:outline-none transition-colors ${
                      item.processing
                        ? 'cursor-default'
                        : 'hover:bg-brand-navy/5 focus:bg-brand-navy/5'
                    }`}
                  >
                    <div className='flex items-start gap-2.5'>
                      {item.processing ? (
                        <ArrowPathIcon className='h-4 w-4 mt-0.5 text-brand-accent flex-shrink-0 animate-spin' />
                      ) : (
                        <CheckCircleIcon className='h-4 w-4 mt-0.5 text-green-600 flex-shrink-0' />
                      )}
                      <div className='min-w-0 flex-1'>
                        <p className='text-sm font-medium text-brand-navy truncate'>
                          {item.patient_name || 'Unknown patient'}
                        </p>
                        <p className='text-xs text-brand-navy/60 mt-0.5'>{labelFor(item)}</p>
                      </div>
                      <span className='text-[11px] text-gray-400 flex-shrink-0 mt-0.5'>
                        {relativeTime(item.created_at)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderNotificationsBell;
